import classnames from 'classnames';
import _ from 'lodash';
import EventHrInfo from './eventHrInfo';
import DailySummaryEventHrInfo from './hrSummary/dailySummaryEventHrInfo';
import useSelectedInfo from './useSelectedInfo';

const EVENT_CLASS_NAME = {
  'Minute info': '--minute-info-clicked',
  Artifact: '--artifact-event-clicked',
  AFib: '--afib-event-clicked',
};

export type SelectedInfoProps = {
  isDailyHrSummary?: boolean;
  timezoneOffset?: number;
};

/**
 * Renders the per-event-type tab strip and detail cards for the events selected on the HR summary chart. Groups the selection with useSelectedInfo, renders one tab per event type showing its count, and switches each panel between the daily-summary and standard HR info card on isDailyHrSummary while remembering the last index viewed per type.
 *
 * @param props - The timezone offset and the isDailyHrSummary switch choosing which info card renders.
 * @returns The event info wrapper with its type tabs and detail cards, or an empty wrapper when nothing is selected.
 */
const SelectedInfo = ({ isDailyHrSummary, timezoneOffset }: SelectedInfoProps) => {
  const {
    selectedColumnInfo,
    aiChannel,
    selectedEventIndex,
    setSelectedEventIndex,
    selectedEvent,
    memoIndexes,
    formatSelectedColumnInfo,
  } = useSelectedInfo();

  return _.isEmpty(selectedColumnInfo) ? (
    <div className="event-info-wrapper" />
  ) : (
    <div id="event-info" className="event-info-wrapper">
      <div className="event-info-tab">
        {_.map(_.keys(formatSelectedColumnInfo), (eventType, index) => {
          const { eventIndexes, events } = formatSelectedColumnInfo[eventType];
          return (
            <button
              key={eventType}
              id={`event-selection-tab-${index}`}
              className={classnames(
                'tab-item',
                (EVENT_CLASS_NAME as any)[eventType] || '--event-clicked',
                _.includes(eventIndexes, selectedEventIndex) ? '--active' : '',
              )}
              onClick={() => {
                const index = memoIndexes.current[eventType];
                setSelectedEventIndex(eventIndexes[index === -1 ? 0 : index]);
              }}
            >
              {`${eventType}${events.length > 1 ? ` (${events.length})` : ''}`}
            </button>
          );
        })}
      </div>
      <div className="event-info-content">
        {_.map(_.keys(formatSelectedColumnInfo), (eventType) => {
          const { eventIndexes, events } = formatSelectedColumnInfo[eventType];
          const defaultIndex = _.findIndex(
            events,
            (event: any) =>
              event?.id === selectedEvent?.id || event?.idEvent === selectedEvent?.idEvent,
          );
          return isDailyHrSummary ? (
            <DailySummaryEventHrInfo
              key={eventType}
              aiChannel={aiChannel}
              isActive={_.includes(eventIndexes, selectedEventIndex)}
              defaultIndex={defaultIndex > -1 ? defaultIndex : 0}
              eventIndexes={eventIndexes}
              events={events}
              timezoneOffset={timezoneOffset}
              setSelectedEventIndex={setSelectedEventIndex}
            />
          ) : (
            <EventHrInfo
              key={eventType}
              aiChannel={aiChannel}
              isActive={_.includes(eventIndexes, selectedEventIndex)}
              defaultIndex={defaultIndex > -1 ? defaultIndex : 0}
              eventIndexes={eventIndexes}
              events={events}
              timezoneOffset={timezoneOffset}
              setSelectedEventIndex={setSelectedEventIndex}
              callbackSetEventIndex={(index) => {
                memoIndexes.current[eventType] = index;
              }}
            />
          );
        })}
      </div>
    </div>
  );
};

export default SelectedInfo;
